import qs from 'qs';
import { getUrlInfo } from '../../lib/url';
import { VIEW_LABELS } from '../../constants/endpoints';
import sendEmail from '../../lib/sendEmail';

export const RECEIVE_PRINTERLESS_RETURN_INFO = 'RECEIVE_PRINTERLESS_RETURN_INFO';
export const TOGGLE_PRINTERLESS_INFO_EMAIL_INPUT = 'TOGGLE_PRINTERLESS_INFO_EMAIL_INPUT';
export const UPDATE_PRINTERLESS_EMAIL_INPUT = 'UPDATE_PRINTERLESS_EMAIL_INPUT';
export const TOGGLE_PRINTERLESS_EMAIL_SENT = 'TOGGLE_PRINTERLESS_EMAIL_SENT';
export const TRIGGER_PRINTERLESS_ERROR_STATE = 'TRIGGER_PRINTERLESS_ERROR_STATE';

const DEFAULT_ITEM_PROPERTIES = ['itemSize', 'itemColor', 'itemStyle', 'returnQty'];

const generatePropertyString = ({ item, language, properties }) =>
  properties.reduce((finalPropertyString, requestKey) => {
    if (!item[requestKey]) return finalPropertyString;

    const displayName = language[`returns.printerless_info_page.${requestKey}`];

    const propertyString = `${displayName}: ${item[requestKey]}`;

    if (!finalPropertyString) return propertyString;

    return `${finalPropertyString} / ${propertyString}`;
  }, '');

const mapItems = ({ items = [], language, itemProperties, itemPropertiesBlockLayout }) =>
  items.map((item) => {
    const { itemName, productSku } = item;
    const properties = itemProperties || DEFAULT_ITEM_PROPERTIES;

    return {
      name: itemName,
      productSku,
      propertiesString: generatePropertyString({ item, language, properties }),
      propertiesBlock: itemPropertiesBlockLayout
        ? properties.map((property) => {
            const label = language[`returns.printerless_info_page.${property}`];
            return `${label}: ${item[property]}`;
          })
        : undefined,
    };
  });

export const requestPrinterlessReturnInfo = () => async (dispatch, getState) => {
  try {
    const {
      page: { locale, productType, browserFingerprint },
      language,
      retailerSettings: { name: retailerName } = {},
      returnsSettings: {
        [productType]: { printerlessInfo: { itemProperties, itemPropertiesBlockLayout } = {} } = {},
      } = {},
    } = getState();

    const { params: { return_id: returnId } = {} } = getUrlInfo();

    const params = qs.stringify({ locale });
    const response = await fetch(VIEW_LABELS({ retailerName, params }), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'narvar-tfid': browserFingerprint,
      },
      body: JSON.stringify({
        return_id: returnId,
      }),
    });

    const {
      label_url: labelUrl = null,
      barcode_url: barcodeUrl = null,
      return_order: {
        return_items: printerlessItems = [],
        email: customerEmail,
        carrier_moniker: carrier = null,
        return_method: orderMethod = null,
        drop_off_location: dropOffLocation = null,
        partner_return_code: partnerReturnCode = '',
      } = {},
      status: { status: responseStatus, message: responseStatusMessage } = {},
    } = await response.json();

    if (responseStatus !== 'SUCCESS') throw new Error(responseStatusMessage);

    const totalItemQuantity = printerlessItems.reduce(
      (totalOfItems, { returnQty: itemQuantity }) => totalOfItems + itemQuantity,
      0,
    );

    const formattedItems = mapItems({
      language,
      itemProperties,
      itemPropertiesBlockLayout,
      items: printerlessItems,
    });

    dispatch({
      type: RECEIVE_PRINTERLESS_RETURN_INFO,
      data: {
        labelUrl,
        barcodeUrl,
        totalItemQuantity,
        items: formattedItems,
        customerEmail,
        carrier,
        orderMethod,
        dropOffLocation,
        partnerReturnCode,
      },
    });
  } catch (err) {
    dispatch({ type: TRIGGER_PRINTERLESS_ERROR_STATE });
    console.error(err); // eslint-disable-line no-console
  }
};

export const togglePrinterlessInfoEmailInput = () => ({
  type: TOGGLE_PRINTERLESS_INFO_EMAIL_INPUT,
});

export const updateEmailInput = (emailInput) => ({
  type: UPDATE_PRINTERLESS_EMAIL_INPUT,
  data: { emailInput },
});

export const emailBarcode = () => async (dispatch, getState) => {
  const {
    retailerSettings: { name: retailerName } = {},
    page: { locale, browserFingerprint } = {},
    printerlessInfo: { emailInput } = {},
  } = getState();

  const { params: { return_id: encryptedReturnId } = {} } = getUrlInfo();

  const emailSuccess = await sendEmail(browserFingerprint)({
    retailerName,
    encryptedReturnId,
    locale,
    email: emailInput,
  });

  if (emailSuccess) {
    dispatch({
      type: TOGGLE_PRINTERLESS_EMAIL_SENT,
      data: { alternateEmailAddress: emailInput },
    });

    dispatch({ type: TOGGLE_PRINTERLESS_INFO_EMAIL_INPUT });
  }

  // TODO: Add error state
};
